import { StyledPropsWithChildren } from '@front10';
import { Context } from '@front10/helpers/dist/Context';
import useStyles from '@front10/helpers/dist/UseStyles';
import React, { useContext } from 'react';

type NativeButtonProps = JSX.IntrinsicElements['button'];
type ButtonSize = 'sm' | 'lg';
export type Color = 'light' | 'dark' | 'primary' | 'link' | 'transparent';

interface Props {
  disabled?: boolean;
  outline?: boolean;
  active?: string;
  ariaLabel?: NativeButtonProps['aria-label'];
  ariaLabelledBy?: NativeButtonProps['aria-labelledby'];
  ariaSelected?: NativeButtonProps['aria-selected'];
  ariaExpanded?: NativeButtonProps['aria-expanded'];
  ariaDescribedBy?: NativeButtonProps['aria-describedby'];
  ariaDisabled?: NativeButtonProps['aria-disabled'];
  ariaHidden?: NativeButtonProps['aria-hidden'];
  ariaCurrent?: NativeButtonProps['aria-current'];
  ariaHaspopup?: NativeButtonProps['aria-haspopup'];
  ariaControls?: NativeButtonProps['aria-controls'];
  tooltip?: NativeButtonProps['title'];
  size?: ButtonSize;
  color?: Color;
  focusReferences?: NativeButtonProps['ref'];
  dataTestId?: string;
  id?: NativeButtonProps['id'];
  dataTooltipId?: string;
  /**
   * Allows the creation of a data-react-cmp-owner data-attr
   */
  dataReactCmpOwner?: string;
}

export type ButtonProps = StyledPropsWithChildren<
  Props &
    Pick<
      NativeButtonProps,
      | 'name'
      | 'role'
      | 'value'
      | 'onClick'
      | 'onFocus'
      | 'onBlur'
      | 'onKeyDown'
      | 'onMouseDown'
      | 'tabIndex'
      | 'type'
    >
>;

function Button(props: ButtonProps) {
  const {
    disabled,
    dataTestId,
    tooltip,
    children,
    ariaLabel,
    ariaHidden,
    ariaLabelledBy,
    ariaSelected,
    ariaExpanded,
    ariaDescribedBy,
    ariaDisabled,
    ariaCurrent,
    ariaHaspopup,
    ariaControls,
    value,
    role,
    type,
    onClick,
    onFocus,
    onBlur,
    color,
    outline,
    name,
    size,
    active,
    onKeyDown,
    onMouseDown,
    dataReactCmpOwner,
    focusReferences,
    tabIndex,
    id,
    dataTooltipId
  } = props;

  const { direction, theme } = useContext(Context);
  const [className, style] = useStyles(props, { direction, theme });
  let cssclass = `Button fr-btn ${className}`;
  if (outline) {
    cssclass += ` fr-btn-outline-${color}`;
  } else {
    cssclass += ` fr-btn-${color}`;
  }
  if (size) {
    cssclass += ` fr-btn-${size}`;
  }
  if (active) {
    cssclass += ` fr-active`;
  }
  const dataAttrs = dataReactCmpOwner ? { 'data-react-cmp-owner': dataReactCmpOwner } : {};

  return (
    <button
      name={name}
      ref={focusReferences}
      title={tooltip}
      disabled={disabled}
      className={cssclass}
      onClick={onClick}
      onFocus={onFocus}
      onBlur={onBlur}
      role={role}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-selected={ariaSelected}
      aria-expanded={ariaExpanded}
      aria-describedby={ariaDescribedBy}
      aria-disabled={ariaDisabled}
      aria-hidden={ariaHidden}
      aria-current={ariaCurrent}
      aria-haspopup={ariaHaspopup}
      aria-controls={ariaControls}
      data-tooltip-id={dataTooltipId}
      value={value}
      type={type}
      style={style}
      onKeyDown={onKeyDown}
      onMouseDown={onMouseDown}
      id={id}
      {...dataAttrs}
      tabIndex={tabIndex}
      data-testid={dataTestId}
    >
      {children}
    </button>
  );
}

Button.defaultProps = {
  disabled: false,
  outline: false,
  active: false,
  type: 'button',
  color: 'light',
  cursor: 'pointer',
  children: null,
  dataReactCmpOwner: null
};

Button.componentName = 'Button';

export default Button;
